import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loader2, Shield, Users, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const RESOURCE_TYPE = 'semantic_model';

// Grant levels map onto the backend object-permission levels (extensions.py
// DEFAULT_OBJECT_PERMISSIONS). "none" removes the grant.
const LEVELS = [
  { value: 'none', label: 'No access' },
  { value: 'viewer', label: 'View' },
  { value: 'editor', label: 'Create & Edit' },
  { value: 'owner', label: 'Delete' },
];

function GranteeRow({ kind, id, name, sub, level, onChange, busy }) {
  const Icon = kind === 'role' ? Shield : UserIcon;
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/30 transition-colors" data-testid={`model-perm-row-${kind}-${id}`}>
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-foreground truncate capitalize">{name}</p>
        {sub && <p className="text-[10px] text-muted-foreground truncate">{sub}</p>}
      </div>
      <Select value={level} onValueChange={(v) => onChange(kind, id, v)} disabled={busy}>
        <SelectTrigger className="h-7 w-[130px] text-[11px] rounded-lg bg-muted/40 border-border/50 shrink-0" data-testid={`model-perm-select-${kind}-${id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LEVELS.map((l) => (
            <SelectItem key={l.value} value={l.value} className="text-xs">{l.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function ModelPermissionsDialog({ model, open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [grants, setGrants] = useState({}); // { 'role:analyst': 'viewer', 'user:user_x': 'editor' }

  const modelId = model?.model_id;

  const load = useCallback(async () => {
    if (!modelId) return;
    setLoading(true);
    // Existing ACL entries — always available to anyone who can view the model.
    try {
      const acls = await api.getObjectPermissions(RESOURCE_TYPE, modelId);
      const g = {};
      (acls || []).forEach((a) => {
        if (a.role) g[`role:${a.role}`] = a.permission_level || 'viewer';
        else if (a.user_id) g[`user:${a.user_id}`] = a.permission_level || 'viewer';
      });
      setGrants(g);
    } catch (e) {
      setGrants({});
    }
    // Roles + users are best-effort (need roles:read / users:read).
    try { const r = await api.getRoles(); setRoles(r || []); } catch (e) { setRoles([]); }
    try { const u = await api.getUsers(); setUsers(u || []); } catch (e) { setUsers([]); }
    setLoading(false);
  }, [modelId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const changeLevel = async (kind, id, level) => {
    const key = `${kind}:${id}`;
    setBusyKey(key);
    try {
      if (level === 'none') {
        await api.removeObjectPermission(RESOURCE_TYPE, modelId, kind === 'role' ? { role: id } : { user_id: id });
        setGrants((prev) => { const n = { ...prev }; delete n[key]; return n; });
        toast.success('Access removed');
      } else {
        await api.setObjectPermission(RESOURCE_TYPE, modelId, {
          ...(kind === 'role' ? { role: id } : { user_id: id }),
          permission_level: level,
        });
        setGrants((prev) => ({ ...prev, [key]: level }));
        toast.success('Access updated');
      }
    } catch (e) {
      toast.error(e.message || 'Could not update access');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg" data-testid="model-permissions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-foreground flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" /> Permissions — {model?.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Give roles or users access to this model: View, Create &amp; Edit or Delete.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground text-xs gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading permissions…
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Users className="w-3 h-3" /> Roles
              </p>
              {roles.length === 0 ? (
                <p className="text-[11px] text-muted-foreground px-2 py-1.5">No roles available.</p>
              ) : (
                <div className="space-y-0.5">
                  {roles.map((r) => (
                    <GranteeRow
                      key={r.role_name}
                      kind="role"
                      id={r.role_name}
                      name={r.role_name}
                      sub={r.description}
                      level={grants[`role:${r.role_name}`] || 'none'}
                      onChange={changeLevel}
                      busy={busyKey === `role:${r.role_name}`}
                    />
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <UserIcon className="w-3 h-3" /> Users
              </p>
              {users.length === 0 ? (
                <p className="text-[11px] text-muted-foreground px-2 py-1.5">No users available (needs Users → View).</p>
              ) : (
                <div className="space-y-0.5">
                  {users.map((u) => (
                    <GranteeRow
                      key={u.user_id}
                      kind="user"
                      id={u.user_id}
                      name={u.name || u.email}
                      sub={u.name ? u.email : (u.role ? `Role: ${u.role}` : '')}
                      level={grants[`user:${u.user_id}`] || 'none'}
                      onChange={changeLevel}
                      busy={busyKey === `user:${u.user_id}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ModelPermissionsDialog;
